import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import {
  addDefaultCircles,
  diagramZoom,
  getDragBounds,
  highlightDiagram,
  isValidZoom,
  zoomToPartNumber
} from './utils';
import { PartDetailsAndSchematicResponse } from './types';
import { CatSkeleton } from 'components/blocks-components';
import { useGesture } from '@use-gesture/react';
import ZoomButtons from './zoom-buttons';
import { MAX_ZOOM, START_ZOOM, ZOOM } from './constants';
import { Conditional, useBreakpoint } from '@cat-ecom/pcc-components';
import cx from 'classnames';
import styles from './parts-diagram.module.scss';
import { PAGE_BREAKPOINTS } from 'constants/common';
import { CartResponse } from 'services/cart/types';
export interface PartsDiagramProps {
  hoveredItem: string | null;
  partDetailsAndSchematicsData: PartDetailsAndSchematicResponse;
  svgContent: string;
  isLoading: boolean;
  isZoomEnabled?: boolean;
  handleOnHover?: (refNumber: string | null) => void;
  isMobile?: boolean;
  cartDetails?: CartResponse;
  isPccPdpPartDiagramUpdateFlag?: boolean;
  isZoomImageEnhancementFlag?: boolean;
  useNewZoomIcons?: boolean;
  onHover?: (referenceNumber: string | null) => void;
  recenterSvgOnPartTapMobile?: boolean;
  isDiagramTabActive?: boolean;
  clickedPartId?: { partId: string; source: 'list' | 'diagram' | null } | null;
  onPartClick?: (partId: string) => void;
}

const PartsDiagram: FC<PartsDiagramProps> = ({
  hoveredItem,
  svgContent,
  isLoading,
  partDetailsAndSchematicsData,
  isZoomEnabled = false,
  handleOnHover,
  isMobile = false,
  cartDetails,
  isPccPdpPartDiagramUpdateFlag = false,
  isZoomImageEnhancementFlag = false,
  useNewZoomIcons = false,
  onHover,
  recenterSvgOnPartTapMobile = false,
  isDiagramTabActive = false,
  clickedPartId,
  onPartClick
}) => {
  const containerRef = useRef(null);
  const isMD = useBreakpoint(PAGE_BREAKPOINTS.MD);
  const [zoom, setZoom] = useState(START_ZOOM);
  const [dragging, setDragging] = useState(false);
  const [dragBounds, setDragBounds] = useState(undefined);
  const clickedOnPartId = clickedPartId?.partId;
  const clickedSource = clickedPartId?.source;

  useEffect(() => {
    if (recenterSvgOnPartTapMobile && hoveredItem && containerRef.current) {
      zoomToPartNumber(
        containerRef.current,
        hoveredItem,
        setZoom,
        setDragBounds,
        isDiagramTabActive,
        ZOOM
      );
    }
  }, [hoveredItem, isDiagramTabActive, recenterSvgOnPartTapMobile]);

  useEffect(() => {
    if (
      isZoomImageEnhancementFlag &&
      clickedOnPartId &&
      clickedSource === 'list' &&
      containerRef.current &&
      isZoomEnabled
    ) {
      zoomToPartNumber(
        containerRef.current,
        clickedOnPartId,
        setZoom,
        setDragBounds,
        isDiagramTabActive,
        2
      );
    }
  }, [
    clickedOnPartId,
    clickedSource,
    isZoomEnabled,
    isZoomImageEnhancementFlag,
    isDiagramTabActive
  ]);

  const handleCircleSelection = useCallback(
    (partItem) => () => {
      if (isZoomImageEnhancementFlag && onPartClick) {
        onPartClick(partItem.referenceNumber);
      }

      if (isMobile && isMD === false) {
        handleOnHover(
          hoveredItem === partItem.referenceNumber
            ? null
            : partItem.referenceNumber
        );
      }
    },
    [
      handleOnHover,
      hoveredItem,
      isMD,
      isMobile,
      onPartClick,
      isZoomImageEnhancementFlag
    ]
  );
  const handleZoom = useCallback(
    (newZoom: number) => {
      if (isValidZoom(newZoom)) {
        diagramZoom(containerRef.current, newZoom);
        setZoom(newZoom);
        const height = containerRef.current.clientHeight;
        const width = containerRef.current.clientWidth;
        const bounds = getDragBounds(height, width, newZoom);
        setDragBounds(bounds);
      }
    },
    [containerRef, setZoom, setDragBounds]
  );

  const handleDrag = (event) => {
    const shouldDragDiagram = zoom > 1 && event.dragging;
    if (shouldDragDiagram) {
      const [offsetX, offsetY] = event.offset;
      containerRef.current.style.transform = `scale3d(${zoom}, ${zoom}, ${zoom}) translate3d(${offsetX}px, ${offsetY}px, 0)`;
    }
  };

  const handlePinch = (state) => {
    const [d] = state.offset;
    const newZoom = d / 100;

    const transform = `scale3d(${newZoom}, ${newZoom}, ${newZoom})`;
    containerRef.current.style.transform = transform;
    return newZoom;
  };

  const handlePinchEnd = ({ memo = 1 }) => {
    handleZoom(memo);
  };
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !isZoomEnabled || !isZoomImageEnhancementFlag) return;

    const handleWheel = (event: WheelEvent) => {
      if (event.ctrlKey) return;
      event.preventDefault();
      const delta = Math.sign(event.deltaY);
      let newZoom = zoom;
      if (delta < 0) {
        newZoom = Math.min(zoom + 1, MAX_ZOOM);
      } else if (delta > 0) {
        newZoom = Math.max(zoom - 1, START_ZOOM);
      }
      if (newZoom !== zoom) {
        handleZoom(newZoom);
      }
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, [zoom, isZoomEnabled, handleZoom, isZoomImageEnhancementFlag]);

  const handleMouseLeave = useCallback(() => {
    onHover(null);
  }, [onHover]);

  useEffect(() => {
    if (containerRef.current) {
      if (hoveredItem?.trim()) {
        highlightDiagram(
          containerRef.current,
          hoveredItem,
          isZoomImageEnhancementFlag
        );
      }
      if (isZoomImageEnhancementFlag && clickedOnPartId) {
        highlightDiagram(
          containerRef.current,
          clickedOnPartId,
          isZoomImageEnhancementFlag,
          true
        );
      }
      partDetailsAndSchematicsData?.lineItems?.map((partItem) => {
        if (partItem.referenceNumber?.trim()) {
          addDefaultCircles(
            containerRef.current,
            partItem.referenceNumber,
            handleCircleSelection(partItem),
            isZoomImageEnhancementFlag &&
              (() => onHover(partItem.referenceNumber)),
            isZoomImageEnhancementFlag && handleMouseLeave,
            hoveredItem === partItem.referenceNumber,
            isZoomImageEnhancementFlag,
            clickedOnPartId === partItem.referenceNumber
          );
        }
      });
    }
  }, [
    hoveredItem,
    partDetailsAndSchematicsData?.lineItems,
    svgContent,
    zoom,
    dragging,
    handleCircleSelection,
    onHover,
    handleMouseLeave,
    isZoomImageEnhancementFlag,
    clickedOnPartId
  ]);

  useGesture(
    {
      onDrag: (state) => handleDrag(state),
      onDragEnd: () => setDragging(false),
      onDragStart: () => setDragging(true),
      onPinch: (state) => handlePinch(state),
      onPinchEnd: (state) => handlePinchEnd(state)
    },
    {
      target: containerRef,
      eventOptions: { passive: false },
      drag: { bounds: dragBounds, threshold: 5 },

      pinch: {
        scaleBounds: { min: 100, max: 1500 }
      }
    }
  );

  return (
    <div
      className={cx('overflow-hidden', {
        'position-relative': !isPccPdpPartDiagramUpdateFlag || isZoomEnabled,
        border: !isPccPdpPartDiagramUpdateFlag,
        'rounded-lg': !isPccPdpPartDiagramUpdateFlag,
        'h-100': isPccPdpPartDiagramUpdateFlag || recenterSvgOnPartTapMobile
      })}
      style={{
        border: isPccPdpPartDiagramUpdateFlag
          ? ''
          : '1px solid var(--cat-theme-color-border-default)',
        borderRadius: isPccPdpPartDiagramUpdateFlag
          ? ''
          : 'var(--cat-border-radius-lg)',
        height:
          isPccPdpPartDiagramUpdateFlag || recenterSvgOnPartTapMobile
            ? '100%'
            : ''
      }}
    >
      {isLoading ? (
        <CatSkeleton />
      ) : (
        <>
          <div
            ref={containerRef}
            dangerouslySetInnerHTML={{ __html: svgContent || '' }}
            className={cx({
              [styles['parts-diagram-wrapper']]:
                isMobile && !isPccPdpPartDiagramUpdateFlag,
              [styles['parts-diagram-body']]:
                !isMD && isPccPdpPartDiagramUpdateFlag,
              [styles['parts-diagram']]: isZoomEnabled,
              [styles['dragging']]: dragging
            })}
            data-testid="partsDiagram"
          />
          <Conditional test={isZoomEnabled}>
            <ZoomButtons
              handleZoom={handleZoom}
              zoom={zoom}
              useNewIcons={useNewZoomIcons}
            />
          </Conditional>
        </>
      )}
    </div>
  );
};

export default PartsDiagram;
